import { Timestamp } from "firebase/firestore";

/**
 * Recursivamente remove propriedades 'undefined' de objetos e arrays para evitar quebras no Firestore.
 */
const cleanUndefined = (obj) => {
    if (obj === null || obj === undefined) return obj;

    if (Array.isArray(obj)) {
        return obj.map(item => cleanUndefined(item));
    }

    if (typeof obj === 'object') {
        // Ignorar Timestamps e outros objetos especiais do Firestore
        if (obj instanceof Timestamp || typeof obj.toDate === 'function') {
            return obj;
        }
        // Se tiver construtor customizado (não Object), não recursar para evitar quebrar classes nativas
        if (obj.constructor && obj.constructor.name !== 'Object') {
            return obj;
        }
        
        const newObj = {};
        Object.keys(obj).forEach(key => {
            if (obj[key] !== undefined) {
                newObj[key] = cleanUndefined(obj[key]);
            }
        });
        return newObj;
    }

    return obj;
};

/**
 * workoutTemplateToFirestore
 * Formulário → Firestore para modelos de treino (workoutTemplates).
 */
export const workoutTemplateToFirestore = (data, tenantData, authUser) => {
    const idTenant = typeof tenantData === 'string' ? tenantData : tenantData?.id;
    if (!idTenant) throw new Error("ID do Tenant é obrigatório.");

    const rawSessions = data.sessions?.length > 0 ? data.sessions : (data.workouts || []);
    const sessions = rawSessions.map(session => ({
        ...session,
        sessionType: session.sessionType || data.workoutType || "strength",
        blocks: (session.blocks || []).map(block => ({
            ...block,
            workoutType: block.workoutType || session.sessionType || data.workoutType || "strength",
        })),
    }));

    const payload = {
        idTenant,
        title: data.title?.trim() || "",
        description: data.description?.trim() || "",
        workoutType: data.workoutType || "strength",
        objective: data.objective || "",
        level: data.level || "",
        tags: Array.isArray(data.tags)
            ? data.tags
            : String(data.tags || "").split(",").map(tag => tag.trim()).filter(Boolean),
        weeklyFrequency: data.weeklyFrequency || null,
        totalWeeks: data.totalWeeks || null,
        isSingleDay: data.isSingleDay || false,
        sessions,

        // Auditoria
        createdAt: data.id ? (data.createdAt || Timestamp.now()) : Timestamp.now(),
        updatedAt: Timestamp.now(),
        createdBy: data.id ? (data.createdBy || null) : {
            uid: authUser?.uid || "system",
            name: authUser?.displayName || authUser?.fullName || authUser?.email || "Sistema"
        },
        updatedBy: {
            uid: authUser?.uid || "system",
            name: authUser?.displayName || authUser?.fullName || authUser?.email || "Sistema"
        }
    };

    return cleanUndefined(payload);
};

/**
 * workoutTemplateFromFirestore
 * Firestore snapshot → React para modelos de treino.
 */
export const workoutTemplateFromFirestore = (snapshot) => {
    if (!snapshot.exists()) return null;
    const data = snapshot.data();

    return {
        id: snapshot.id,
        ...data,
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
        updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt,
    };
};

/**
 * workoutPrescriptionToFirestore
 * Formulário → Firestore para prescrições de treino.
 */
export const workoutPrescriptionToFirestore = (prescriptionBase, member, tenantData, authUser, teacherData = {}) => {
    const idTenant = typeof tenantData === 'string' ? tenantData : tenantData?.id;
    if (!idTenant) throw new Error("ID do Tenant é obrigatório.");

    // Calcular total de sessões previstas
    const start = new Date(prescriptionBase.startDate);
    const end = new Date(prescriptionBase.endDate);
    const diffDays = Math.ceil(Math.abs(end - start) / (1000 * 60 * 60 * 24));
    const totalWeeks = Math.max(1, diffDays / 7);
    const totalExpectedSessions = Math.round(totalWeeks * (prescriptionBase.weeklyFrequency || 3));

    // Limpar id vazio para evitar quebra no map
    const baseData = { ...prescriptionBase };
    delete baseData.id;

    const sessions = prescriptionBase.sessions?.length > 0
        ? prescriptionBase.sessions
        : (prescriptionBase.workouts || []);

    const payload = {
        ...baseData,
        sessions,

        // Dados do Aluno
        idMember: member.id,
        memberName: member.name,
        memberPhoto: member.photo || member.photoUrl || teacherData.memberPhoto || null,
        memberEmail: member.email || teacherData.memberEmail || null,
        memberPhone: member.phone || teacherData.memberPhone || null,

        // Dados do Professor (Prescritor)
        idTeacher: authUser?.uid || null,
        teacherName: teacherData.teacherName || authUser?.displayName || authUser?.email || "Sistema",
        teacherPhotoUrl: teacherData.teacherPhoto || null,
        teacherEmail: teacherData.teacherEmail || authUser?.email || null,
        teacherPhone: teacherData.teacherPhone || null,

        status: "active",
        idTenant,

        // Metadados de Execução
        completedSessionsCount: 0,
        totalExpectedSessions,
        lastCompletedWorkoutId: null,
        lastCompletedWorkoutName: null,
        lastCompletedAt: null,

        // Auditoria
        prescribedAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        createdBy: {
            uid: authUser?.uid || "system",
            name: teacherData.teacherName || authUser?.displayName || authUser?.fullName || authUser?.email || "Sistema"
        },
        updatedBy: {
            uid: authUser?.uid || "system",
            name: teacherData.teacherName || authUser?.displayName || authUser?.fullName || authUser?.email || "Sistema"
        }
    };

    return cleanUndefined(payload);
};

/**
 * workoutPrescriptionFromFirestore
 * Firestore snapshot → React para prescrições.
 */
export const workoutPrescriptionFromFirestore = (snapshot) => {
    if (!snapshot.exists?.()) {
        // Suporte a formato { id, ...data } direto (getDocs map)
        return snapshot;
    }
    const data = snapshot.data();

    return {
        id: snapshot.id,
        ...data,
        prescribedAt: data.prescribedAt?.toDate ? data.prescribedAt.toDate() : data.prescribedAt,
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
        updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt,
    };
};
